// Demo store — single source of truth for the demo session.
//
// Demo mode flag, full inbox, and backup live in localStorage so all tabs
// share the underlying demo data and exit propagates via storage events.
// Per-tab identity (role + the email this tab represents) lives in
// sessionStorage so each tab can render its own filtered inbox view.

import { useSyncExternalStore } from 'react';
import { DEMO_LIMITS } from './config';

export type DemoEmailKind =
  | 'school_otp'
  | 'school_invite'
  | 'teacher_invite'
  | 'cover_assignment'
  | 'cover_bulk'
  | 'password_reset'
  | 'secondary_contact_invite'
  | 'teacher_termination'
  | 'retention_review'
  | 'retrieval_request'
  | 'other';

export interface DemoEmail {
  id: string;
  kind: DemoEmailKind;
  toEmail: string;
  toName?: string;
  subject: string;
  body: string;
  // Optional call-to-action — rendered as a button in the inbox detail
  // view. In demo mode every invite/notification points at a /demo/ URL
  // so clicking it opens the right tab in demo mode (not the real app).
  cta?: { label: string; url: string };
  meta?: Record<string, unknown>;
  receivedAt: number;
  read: boolean;
}

export interface DemoToast {
  id: string;
  level: 'info' | 'warn';
  message: string;
  createdAt: number;
}

// Lock modal — centered overlay used for two cases:
//   1. Demo-mode restrictions (3rd teacher, blurred email click).
//   2. Demo-mode hand-holding ("you just submitted a lesson — switch to
//      the admin tab to review it"). Set tabSwitch + actionLabel for the
//      animated walk-through variant.
export interface DemoLock {
  title: string;
  message: string;
  emoji?: string;
  // When set, renders an animated browser-tab-switch graphic so the user
  // visually understands which tab to switch to next.
  tabSwitch?: { fromLabel: string; toLabel: string };
  // Override the dismiss button label (default: "Got it"). When `openUrl`
  // is set, clicking the button also opens that URL in a new tab before
  // dismissing — useful for "Open Marcus's tab" style flows.
  actionLabel?: string;
  openUrl?: string;
}

interface DemoState {
  active: boolean;
  role: 'school' | 'teacher' | 'cover' | null;
  tabUserEmail: string | null; // identity this tab represents
  inbox: DemoEmail[];
  toasts: DemoToast[];
  lock: DemoLock | null;
  // Controlled inbox panel — the wizard celebration modal flips this to
  // true so the user lands directly on their pending invites.
  inboxOpen: boolean;
  /**
   * One-time "this is your mailbox, and your code is in it" spotlight.
   *
   * Fires the first time the demo sends an email the user has to go and READ —
   * the wizard's verification code. Until that moment the inbox is a floating
   * button nobody has a reason to press; the code is the first thing that makes
   * it matter, so that is the one moment worth interrupting for. Deliberately
   * once per demo run: the same interruption on every later email would be the
   * thing it is trying to prevent.
   */
  mailboxIntro: boolean;
}

const INBOX_KEY = 'cmc_demo_inbox_v1';
const MODE_KEY = 'cmc_demo_mode';
const ROLE_KEY = 'cmc_demo_role';
const TAB_EMAIL_KEY = 'cmc_demo_tab_email';
/** Set once the mailbox spotlight has been shown; cleared on demo entry. */
const MAILBOX_INTRO_KEY = 'cmc_demo_mailbox_intro_v1';

function loadInbox(): DemoEmail[] {
  try {
    const raw = localStorage.getItem(INBOX_KEY);
    return raw ? (JSON.parse(raw) as DemoEmail[]) : [];
  } catch {
    return [];
  }
}

function saveInbox(inbox: DemoEmail[]) {
  try {
    localStorage.setItem(INBOX_KEY, JSON.stringify(inbox));
  } catch {
    // ignore
  }
}

function loadRole(): 'school' | 'teacher' | 'cover' | null {
  try {
    const r = sessionStorage.getItem(ROLE_KEY);
    if (r === 'school' || r === 'teacher' || r === 'cover') return r;
  } catch {
    // ignore
  }
  return null;
}

function loadTabEmail(): string | null {
  try {
    return sessionStorage.getItem(TAB_EMAIL_KEY);
  } catch {
    return null;
  }
}

let state: DemoState = {
  active: isDemoMode(),
  role: loadRole(),
  tabUserEmail: loadTabEmail(),
  inbox: loadInbox(),
  toasts: [],
  lock: null,
  inboxOpen: false,
  mailboxIntro: false,
};

const listeners = new Set<() => void>();

function emit() {
  saveInbox(state.inbox);
  listeners.forEach((l) => l());
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

function getSnapshot() {
  return state;
}

if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === INBOX_KEY) {
      state = { ...state, inbox: loadInbox() };
      listeners.forEach((l) => l());
    } else if (e.key === MODE_KEY) {
      state = { ...state, active: isDemoMode() };
      listeners.forEach((l) => l());
    }
  });
}

// ── Public API ─────────────────────────────────────────────────────────

/**
 * Is a demo session actually running?
 *
 * Deliberately NOT isDemoMode(): that one also returns true for any /demo
 * pathname, which makes it useless to the /demo chooser itself — the chooser
 * would always believe it was inside a demo. This reads only the flag that
 * enterDemoSession sets and exitDemoSession clears, so it answers "is there a
 * demo session whose state I may trust and display", which is the question a
 * surface asks before rendering anything it read out of shared storage.
 */
export function hasActiveDemoSession(): boolean {
  try {
    return localStorage.getItem(MODE_KEY) === '1';
  } catch {
    return false;
  }
}

export function isDemoMode(): boolean {
  try {
    if (localStorage.getItem(MODE_KEY) === '1') return true;
  } catch {
    // ignore
  }
  if (typeof window !== 'undefined' && window.location?.pathname?.startsWith('/demo')) {
    return true;
  }
  return false;
}

export function activateDemo(role: 'school' | 'teacher' | 'cover', tabUserEmail: string | null = null) {
  try {
    localStorage.setItem(MODE_KEY, '1');
    sessionStorage.setItem(ROLE_KEY, role);
    if (tabUserEmail) sessionStorage.setItem(TAB_EMAIL_KEY, tabUserEmail);
    // A fresh demo run is a fresh first impression, so the mailbox spotlight is
    // owed again. Keyed in localStorage rather than memory so a page reload
    // mid-wizard doesn't re-show it.
    localStorage.removeItem(MAILBOX_INTRO_KEY);
    // Same reasoning: the demo policy assistant is capped at three questions,
    // and a counter that outlived the run meant the second walkthrough in a tab
    // opened with "Demo questions used up" before anyone had asked one.
    sessionStorage.removeItem('cmc_demo_policy_chat_count');
    sessionStorage.removeItem('cmc_tour_policy_chat_count');
  } catch {
    // ignore
  }
  state = { ...state, active: true, role, tabUserEmail: tabUserEmail ?? state.tabUserEmail, mailboxIntro: false };
  emit();
}

export function setTabUserEmail(email: string | null) {
  try {
    if (email) sessionStorage.setItem(TAB_EMAIL_KEY, email);
    else sessionStorage.removeItem(TAB_EMAIL_KEY);
  } catch {
    // ignore
  }
  state = { ...state, tabUserEmail: email };
  emit();
}

export function deactivateDemo() {
  try {
    localStorage.removeItem(MODE_KEY);
    localStorage.removeItem(INBOX_KEY);
    // demo-only sidecar ledgers — wipe so the next demo starts clean
    localStorage.removeItem('cmc_demo_cover_assignments_v1');
    // Account-level tour memory is demo state too. Without this the first
    // prospect to skip a tour kills it for every later demo on that machine —
    // the flag is one-way by design, so nothing else would ever bring it back.
    localStorage.removeItem('cmc_demo_tours_seen_v1');
    localStorage.removeItem('cmc_demo_classes_v1');
    localStorage.removeItem('cmc_demo_pupils_v1');
    // Retention tour hand-off flag — a stale one from a previous demo
    // session must not auto-open Settings or auto-launch the retention
    // tutorial on a fresh entry that never went through the Classes tour.
    sessionStorage.removeItem(ROLE_KEY);
    sessionStorage.removeItem(TAB_EMAIL_KEY);
  } catch {
    // ignore
  }
  state = {
    active: false,
    role: null,
    tabUserEmail: null,
    inbox: [],
    toasts: [],
    lock: null,
    inboxOpen: false,
    mailboxIntro: false,
  };
  emit();
}

export function openInbox() {
  state = { ...state, inboxOpen: true };
  emit();
}

export function closeInbox() {
  state = { ...state, inboxOpen: false };
  emit();
}

let uidCounter = 0;
function uid(prefix: string) {
  uidCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${uidCounter}`;
}

export function pushInboxEmail(email: Omit<DemoEmail, 'id' | 'receivedAt' | 'read'>) {
  const next: DemoEmail = {
    ...email,
    id: uid('email'),
    receivedAt: Date.now(),
    read: false,
  };
  let inbox = [next, ...state.inbox];
  if (inbox.length > DEMO_LIMITS.inboxItems) {
    inbox = inbox.slice(0, DEMO_LIMITS.inboxItems);
    pushToast('warn', `Demo inbox capped at ${DEMO_LIMITS.inboxItems} items, oldest removed.`);
  }
  state = { ...state, inbox };
  emit();
}

export function markEmailRead(id: string) {
  state = {
    ...state,
    inbox: state.inbox.map((e) => (e.id === id ? { ...e, read: true } : e)),
  };
  emit();
}

export function markAllRead() {
  state = { ...state, inbox: state.inbox.map((e) => ({ ...e, read: true })) };
  emit();
}

export function clearInbox() {
  state = { ...state, inbox: [] };
  emit();
}

export function pushToast(level: 'info' | 'warn', message: string) {
  const toast: DemoToast = { id: uid('toast'), level, message, createdAt: Date.now() };
  state = { ...state, toasts: [...state.toasts, toast] };
  emit();
  setTimeout(() => dismissToast(toast.id), 4000);
}

export function dismissToast(id: string) {
  state = { ...state, toasts: state.toasts.filter((t) => t.id !== id) };
  emit();
}

/**
 * Show the mailbox spotlight, unless this demo run has already had it.
 * Safe to call from anywhere that sends a demo email — the guard is here rather
 * than at every call site.
 */
export function showMailboxIntro() {
  try {
    if (localStorage.getItem(MAILBOX_INTRO_KEY) === '1') return;
    localStorage.setItem(MAILBOX_INTRO_KEY, '1');
  } catch {
    // Private browsing: show it, and accept that a reload may show it twice.
  }
  state = { ...state, mailboxIntro: true };
  emit();
}

export function dismissMailboxIntro() {
  if (!state.mailboxIntro) return;
  state = { ...state, mailboxIntro: false };
  emit();
}

export function showDemoLock(lock: DemoLock) {
  state = { ...state, lock };
  emit();
}

export function closeDemoLock() {
  state = { ...state, lock: null };
  emit();
}

// ── React bindings ─────────────────────────────────────────────────────

export function useDemo(): DemoState {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

// Per-tab inbox view — filters the shared store by the email address this
// tab represents. School admin tab sees emails to test-admin@example.com,
// teacher tabs see emails addressed to their specific test-teacher-N
// email, so opening a teacher invite link in a new tab gives them a
// fresh, focused inbox.
export function useFilteredInbox(): { inbox: DemoEmail[]; unread: number; total: number } {
  const s = useDemo();
  const targetEmail = s.tabUserEmail?.toLowerCase() ?? null;
  // No identity yet → show everything (e.g. /demo landing page).
  const inbox = targetEmail
    ? s.inbox.filter((e) => e.toEmail.toLowerCase() === targetEmail)
    : s.inbox;
  return {
    inbox,
    unread: inbox.filter((e) => !e.read).length,
    total: inbox.length,
  };
}
